import React from 'react';
import axios from 'axios';
import { GoogleMap, LoadScript, Marker, Polyline, InfoWindow, GoogleMarkerClusterer } from '@react-google-maps/api';
import { baseURL } from '../api/baseURL';
import { connect } from 'react-redux';
import AdminShell from './layout/AdminShell';
import CallButton from './CallButton';
import { chipBarStyle, chipStyle, pillBarStyle, pillStyle, countPillStyle } from './layout/listUi';

const POLL_INTERVAL_MS = 15000;
const MAPS_JS_KEY = process.env.REACT_APP_GOOGLE_MAPS_JS_KEY || '';

// Default center: Mumbai. Only used before any marker exists.
const DEFAULT_CENTER = { lat: 19.076, lng: 72.8777 };

const REACH_COLOR: Record<string, string> = {
    enroute: '#f0ad4e',
    arrived: '#5cb85c',
    wrapped_present: '#5bc0de',
    wrapped_left: '#8c8c8c',
    signal_off: '#d9534f',
};
const REACH_LABEL: Record<string, string> = {
    enroute: 'En route',
    arrived: 'On location · shooting',
    wrapped_present: 'Wrapped · still on-site',
    wrapped_left: 'Wrapped · left location',
    signal_off: 'Location off',
};
const VENUE_COLOR = '#8e44ad';
const IDLE_AVAILABLE_COLOR = '#1e88ff';
const IDLE_UNAVAILABLE_COLOR = '#6b7280';

const svgUrl = (svg: string) => `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;

// Solid, opaque teardrop pins (white outline so they read on both the map and
// satellite view) — the old faded PNG markers were hard to see and hard to
// tell apart. Cached so a marker's icon object stays stable across re-renders.
const pinIconCache: Record<string, any> = {};
function pinIcon(color: string): any {
    const gm = (window as any).google?.maps;
    if (!gm) return undefined;
    if (!pinIconCache[color]) {
        const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="30" height="40" viewBox="0 0 30 40"><path d="M15 1C7.3 1 1.5 6.9 1.5 14.2c0 10 13.5 24.8 13.5 24.8s13.5-14.8 13.5-24.8C28.5 6.9 22.7 1 15 1z" fill="${color}" stroke="#ffffff" stroke-width="2"/><circle cx="15" cy="14.5" r="5" fill="#ffffff"/></svg>`;
        pinIconCache[color] = { url: svgUrl(svg), scaledSize: new gm.Size(30, 40), anchor: new gm.Point(15, 40) };
    }
    return pinIconCache[color];
}

// When many pins overlap (e.g. 89 photographers across Mumbai) they merge into
// a numbered bubble that splits apart as you zoom in. One clusterer per group
// keeps the bubble colour matching the pins inside it. Defined once so the
// options object is stable between renders.
const makeClusterOptions = (color: string): any => ({
    renderer: {
        render: ({ count, position }: any) => {
            const gm = (window as any).google.maps;
            const size = count < 10 ? 38 : count < 30 ? 46 : 54;
            const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}"><circle cx="${size / 2}" cy="${size / 2}" r="${size / 2 - 2}" fill="${color}" stroke="#ffffff" stroke-width="3"/></svg>`;
            return new gm.Marker({
                position,
                icon: { url: svgUrl(svg), scaledSize: new gm.Size(size, size), anchor: new gm.Point(size / 2, size / 2) },
                label: { text: String(count), color: '#ffffff', fontSize: '13px', fontWeight: '700' },
                zIndex: 1000 + count,
            });
        },
    },
});
const AVAILABLE_CLUSTER_OPTIONS = makeClusterOptions(IDLE_AVAILABLE_COLOR);
const UNAVAILABLE_CLUSTER_OPTIONS = makeClusterOptions(IDLE_UNAVAILABLE_COLOR);

const EMPTY_VIEW_TEXT: Record<string, string> = {
    shoots: 'No active shoots right now',
    off: 'No photographers with location off',
    available: 'No available photographers',
    unavailable: 'No unavailable photographers',
};

type ActiveShoot = {
    receiptId: string;
    customerName: string | null;
    categoryName: string | null;
    address: string | null;
    venue: { lat: number; lng: number } | null;
    photographer: {
        id: string; name: string | null; contact: string | null;
        lat: number | null; lng: number | null; updatedAt: string | null; rating: number | null;
    };
    reach: string;
    distanceMeters: number | null;
};

type IdlePhotographer = {
    id: string; name: string | null; contact: string | null; lat: number | null; lng: number | null; rating: number | null;
};

type MapFilter = 'all' | 'shoots' | 'available' | 'unavailable' | 'off';

type State = {
    activeShoots: ActiveShoot[];
    idleAvailable: IdlePhotographer[];
    idleUnavailable: IdlePhotographer[];
    err: boolean;
    mapFilter: MapFilter;
    // Pins use google.maps.Size/Point for their icons, so they are only drawn once
    // the Maps script has actually loaded (avoids a flash of default red pins).
    mapReady: boolean;
    selectedKey: string | null;
};

/**
 * Live Map page — routed at /live-map. Phase 3 of the admin-dashboard.html
 * redesign: rebuilds the earlier flat "one marker per photographer" view
 * into venue+photographer marker pairs connected by a line, colored by
 * "reach" status (computed server-side, see Admin Server's
 * src/helpers/liveMapReach.js), plus an idle roster (available/unavailable)
 * and filter chips. Polls GET /live-map-data every 15s.
 */
class LiveTrackingMap extends React.Component<{ token?: string }, State> {
    pollTimer: ReturnType<typeof setInterval> | null = null;
    mapInstance: any = null;
    // Auto-fit the map to every marker exactly once, on first data load —
    // not on every 15s poll, so it doesn't fight an admin who has manually
    // panned/zoomed. Without this the map fell back to centering on
    // whichever idle photographer happened to be first in the list (could
    // be anywhere in Greater Mumbai), at a fixed zoom, pushing everyone
    // else off-screen — a real bug found in review.
    hasFitBounds = false;

    state: State = {
        activeShoots: [],
        idleAvailable: [],
        idleUnavailable: [],
        err: false,
        mapFilter: 'all',
        mapReady: false,
        selectedKey: null,
    };

    componentDidMount(): void {
        this.startPolling();
    }

    componentWillUnmount(): void {
        this.stopPolling();
    }

    startPolling = (): void => {
        this.refresh();
        if (!this.pollTimer) {
            this.pollTimer = setInterval(() => this.refresh(), POLL_INTERVAL_MS);
        }
    };

    stopPolling = (): void => {
        if (this.pollTimer) {
            clearInterval(this.pollTimer);
            this.pollTimer = null;
        }
    };

    refresh = (): void => {
        const token = this.props.token;
        if (!token) return;
        axios
            .get(`${baseURL}/live-map-data`, {
                headers: { Authorization: `Bearer ${token}` },
            })
            .then((response: any) => {
                this.setState({
                    activeShoots: response?.data?.activeShoots || [],
                    idleAvailable: response?.data?.idleAvailable || [],
                    idleUnavailable: response?.data?.idleUnavailable || [],
                    err: false,
                }, () => this.fitBoundsIfNeeded());
            })
            .catch(() => {
                this.setState({ err: true });
            });
    };

    onMapLoad = (map: any): void => {
        this.mapInstance = map;
        this.setState({ mapReady: true }, () => this.fitBoundsIfNeeded());
    };

    /** Frames the map on whatever the current filter is showing (venues, live
     * photographer positions and idle pins). Returns false when there is
     * nothing to frame, so the view is left where it is. */
    fitToVisible = (): boolean => {
        if (!this.mapInstance || !(window as any).google) return false;
        const points: { lat: number; lng: number }[] = [];
        this.visibleShoots().forEach((s) => {
            if (s.venue) points.push(s.venue);
            if (s.photographer.lat != null && s.photographer.lng != null) {
                points.push({ lat: s.photographer.lat, lng: s.photographer.lng });
            }
        });
        const { available, unavailable } = this.visibleIdle();
        [...available, ...unavailable].forEach((p) => {
            if (p.lat != null && p.lng != null) points.push({ lat: p.lat, lng: p.lng });
        });
        if (points.length === 0) return false;

        const gm = (window as any).google.maps;
        const bounds = new gm.LatLngBounds();
        points.forEach((pt) => bounds.extend(pt));
        this.mapInstance.fitBounds(bounds, 48);
        // A single pin (or several on one spot) would otherwise zoom to street level.
        gm.event.addListenerOnce(this.mapInstance, 'idle', () => {
            if (this.mapInstance.getZoom() > 15) this.mapInstance.setZoom(15);
        });
        return true;
    };

    /** First data load only — later 15s polls must not fight an admin who has
     * panned/zoomed. Filter changes re-frame explicitly via setFilter. */
    fitBoundsIfNeeded = (): void => {
        if (this.hasFitBounds) return;
        if (this.fitToVisible()) this.hasFitBounds = true;
    };

    /** Single source of truth for the chips above the map AND the
     * Active/Available/Unavailable tabs beside it, so changing either one
     * changes the map, and the map re-frames on what is now shown. */
    setFilter = (key: MapFilter): void => {
        this.setState({ mapFilter: key, selectedKey: null }, () => this.fitToVisible());
    };

    focusPoint = (lat: number | null, lng: number | null, zoom: number, selectedKey: string): void => {
        if (lat == null || lng == null || !this.mapInstance) return;
        this.mapInstance.panTo({ lat, lng });
        this.mapInstance.setZoom(zoom);
        this.setState({ selectedKey });
    };

    visibleShoots = (): ActiveShoot[] => {
        const { activeShoots, mapFilter } = this.state;
        if (mapFilter === 'available' || mapFilter === 'unavailable') return [];
        if (mapFilter === 'off') return activeShoots.filter((s) => s.reach === 'signal_off');
        return activeShoots; // 'all' and 'shoots' both show every active shoot
    };

    visibleIdle = (): { available: IdlePhotographer[]; unavailable: IdlePhotographer[] } => {
        const { idleAvailable, idleUnavailable, mapFilter } = this.state;
        if (mapFilter === 'shoots' || mapFilter === 'off') return { available: [], unavailable: [] };
        if (mapFilter === 'available') return { available: idleAvailable, unavailable: [] };
        if (mapFilter === 'unavailable') return { available: [], unavailable: idleUnavailable };
        return { available: idleAvailable, unavailable: idleUnavailable };
    };

    render() {
        const { activeShoots, idleAvailable, idleUnavailable, err, mapFilter, selectedKey } = this.state;
        const rosterTab = mapFilter === 'available' ? 'available' : mapFilter === 'unavailable' ? 'unavailable' : 'shoots';
        const onLocationCount = activeShoots.filter((s) => s.reach === 'arrived' || s.reach === 'wrapped_present').length;
        const enrouteCount = activeShoots.filter((s) => s.reach === 'enroute').length;
        const offCount = activeShoots.filter((s) => s.reach === 'signal_off').length;
        const visibleShoots = this.visibleShoots();
        const { available: visibleAvailable, unavailable: visibleUnavailable } = this.visibleIdle();
        const hasCoords = (p: { lat: number | null; lng: number | null }) => p.lat != null && p.lng != null;
        const drawShoots = this.state.mapReady ? visibleShoots : [];
        const drawAvailable = this.state.mapReady ? visibleAvailable : [];
        const drawUnavailable = this.state.mapReady ? visibleUnavailable : [];
        const visiblePinCount =
            visibleShoots.filter((s) => s.venue || (s.photographer.lat != null && s.photographer.lng != null)).length +
            visibleAvailable.filter(hasCoords).length + visibleUnavailable.filter(hasCoords).length;
        const selectedIdle = this.state.mapReady && selectedKey?.startsWith('idle-')
            ? [
                ...visibleAvailable.map((p) => ({ p, label: 'Available', color: IDLE_AVAILABLE_COLOR })),
                ...visibleUnavailable.map((p) => ({ p, label: 'Unavailable', color: IDLE_UNAVAILABLE_COLOR })),
            ].find((x) => `idle-${x.p.id}` === selectedKey && hasCoords(x.p))
            : null;

        return (
            <AdminShell activePage="live-map" title="Live map" subtitle="Shoots &amp; photographers · tracking window is 30 min before/after each booking">
                {!MAPS_JS_KEY ? (
                    <div className="card"><div className="card-body">
                        <p style={{ color: '#c0392b' }}>Live map isn't configured yet — REACT_APP_GOOGLE_MAPS_JS_KEY is not set for this deployment.</p>
                    </div></div>
                ) : err ? (
                    <div className="card"><div className="card-body">
                        <p style={{ color: '#c0392b' }}>Couldn't load live map data, please try again shortly.</p>
                    </div></div>
                ) : (
                    <>
                        {/* KPI row */}
                        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 16 }}>
                            {[
                                { dot: REACH_COLOR.arrived, label: 'On location · shooting', value: onLocationCount },
                                { dot: REACH_COLOR.enroute, label: 'En route to shoot', value: enrouteCount },
                                { dot: REACH_COLOR.signal_off, label: 'Location off (on a shoot)', value: offCount },
                                { dot: '#0090e7', label: 'Available & idle', value: idleAvailable.length },
                            ].map((k) => (
                                <div key={k.label} style={{
                                    display: 'flex', alignItems: 'center', gap: 12, flex: '1 1 200px',
                                    background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
                                    borderRadius: 14, padding: '14px 16px',
                                }}>
                                    <span style={{ width: 11, height: 11, borderRadius: '50%', background: k.dot, flexShrink: 0 }} />
                                    <div>
                                        <div style={{ fontSize: 22, fontWeight: 800 }}>{k.value}</div>
                                        <div style={{ fontSize: 11, fontWeight: 700, opacity: 0.65 }}>{k.label}</div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {offCount > 0 ? (
                            <div style={{
                                display: 'flex', alignItems: 'center', gap: 10, background: 'rgba(217,83,79,0.1)',
                                border: '1px solid rgba(217,83,79,0.4)', borderRadius: 11, padding: '10px 14px', marginBottom: 14,
                            }}>
                                <i className="mdi mdi-alert-outline" style={{ color: REACH_COLOR.signal_off, fontSize: 18 }} />
                                <span style={{ fontSize: 12.5, fontWeight: 700, color: REACH_COLOR.signal_off }}>
                                    {offCount} photographer{offCount === 1 ? ' has' : 's have'} their location off during a shoot
                                </span>
                            </div>
                        ) : null}

                        <div style={{ display: 'grid', gridTemplateColumns: '8fr 4fr', gap: 16, alignItems: 'start' }}>
                            {/* Map card */}
                            <div className="card">
                                <div className="card-body">
                                    <div style={chipBarStyle}>
                                        {([
                                            { key: 'all', label: 'All' },
                                            { key: 'shoots', label: 'Active shoots' },
                                            { key: 'available', label: 'Available' },
                                            { key: 'unavailable', label: 'Unavailable' },
                                            { key: 'off', label: 'Location off' },
                                        ] as { key: MapFilter; label: string }[]).map((chip) => (
                                            <span key={chip.key} style={chipStyle(mapFilter === chip.key)} onClick={() => this.setFilter(chip.key)}>
                                                {chip.label}
                                            </span>
                                        ))}
                                    </div>
                                    <div style={{ position: 'relative' }}>
                                    <LoadScript googleMapsApiKey={MAPS_JS_KEY}>
                                        <GoogleMap
                                            mapContainerStyle={{ width: '100%', height: 520, borderRadius: 12 }}
                                            center={DEFAULT_CENTER}
                                            zoom={11}
                                            onLoad={this.onMapLoad}
                                        >
                                            {drawShoots.map((s) => (
                                                <React.Fragment key={s.receiptId}>
                                                    {s.venue ? (
                                                        <Marker
                                                            position={s.venue}
                                                            icon={pinIcon(VENUE_COLOR)}
                                                            title={`Shoot venue · ${s.customerName || ''}`}
                                                            onClick={() => this.setState({ selectedKey: `venue-${s.receiptId}` })}
                                                        >
                                                            {selectedKey === `venue-${s.receiptId}` ? (
                                                                <InfoWindow onCloseClick={() => this.setState({ selectedKey: null })}>
                                                                    <div style={{ minWidth: 170 }}>
                                                                        <strong>Shoot · {s.customerName || 'Customer'}</strong>
                                                                        <div style={{ fontSize: 12, color: '#666' }}>{s.categoryName}</div>
                                                                        <div style={{ fontSize: 12, color: '#666' }}>{s.address}</div>
                                                                        <div style={{ fontSize: 11, color: '#999', marginTop: 4 }}>#{s.receiptId}</div>
                                                                    </div>
                                                                </InfoWindow>
                                                            ) : null}
                                                        </Marker>
                                                    ) : null}
                                                    {s.photographer.lat != null && s.photographer.lng != null ? (
                                                        <Marker
                                                            position={{ lat: s.photographer.lat, lng: s.photographer.lng }}
                                                            icon={pinIcon(REACH_COLOR[s.reach] || '#8c8c8c')}
                                                            title={`${s.photographer.name || 'Photographer'} — ${REACH_LABEL[s.reach] || s.reach}`}
                                                            onClick={() => this.setState({ selectedKey: `photog-${s.receiptId}` })}
                                                        >
                                                            {selectedKey === `photog-${s.receiptId}` ? (
                                                                <InfoWindow onCloseClick={() => this.setState({ selectedKey: null })}>
                                                                    <div style={{ minWidth: 170 }}>
                                                                        <strong>{s.photographer.name || 'Photographer'}</strong>
                                                                        <div style={{ fontSize: 12, color: REACH_COLOR[s.reach] || '#666', fontWeight: 700 }}>
                                                                            {REACH_LABEL[s.reach] || s.reach}
                                                                        </div>
                                                                        {s.distanceMeters != null ? (
                                                                            <div style={{ fontSize: 12, color: '#666' }}>{Math.round(s.distanceMeters)}m from venue</div>
                                                                        ) : null}
                                                                        <div style={{ fontSize: 11, color: '#999', marginTop: 4 }}>#{s.receiptId}</div>
                                                                    </div>
                                                                </InfoWindow>
                                                            ) : null}
                                                        </Marker>
                                                    ) : null}
                                                    {s.venue && s.photographer.lat != null && s.photographer.lng != null ? (
                                                        <Polyline
                                                            path={[s.venue, { lat: s.photographer.lat, lng: s.photographer.lng }]}
                                                            options={{
                                                                strokeColor: REACH_COLOR[s.reach] || '#999',
                                                                strokeOpacity: 0.7,
                                                                strokeWeight: 2,
                                                            }}
                                                        />
                                                    ) : null}
                                                </React.Fragment>
                                            ))}
                                            {/* Both clusterers stay mounted (the library never disposes one on unmount);
                                                a hidden group just gets an empty marker list. */}
                                            <GoogleMarkerClusterer options={UNAVAILABLE_CLUSTER_OPTIONS}>
                                                    {(clusterer) => (
                                                        <>
                                                            {drawUnavailable.map((p) => hasCoords(p) ? (
                                                                <Marker
                                                                    key={`idle-u-${p.id}`}
                                                                    position={{ lat: p.lat as number, lng: p.lng as number }}
                                                                    icon={pinIcon(IDLE_UNAVAILABLE_COLOR)}
                                                                    zIndex={1}
                                                                    clusterer={clusterer}
                                                                    title={`${p.name || 'Photographer'} — Unavailable`}
                                                                    onClick={() => this.setState({ selectedKey: `idle-${p.id}` })}
                                                                />
                                                            ) : null)}
                                                        </>
                                                    )}
                                            </GoogleMarkerClusterer>
                                            <GoogleMarkerClusterer options={AVAILABLE_CLUSTER_OPTIONS}>
                                                    {(clusterer) => (
                                                        <>
                                                            {drawAvailable.map((p) => hasCoords(p) ? (
                                                                <Marker
                                                                    key={`idle-a-${p.id}`}
                                                                    position={{ lat: p.lat as number, lng: p.lng as number }}
                                                                    icon={pinIcon(IDLE_AVAILABLE_COLOR)}
                                                                    zIndex={2}
                                                                    clusterer={clusterer}
                                                                    title={`${p.name || 'Photographer'} — Available`}
                                                                    onClick={() => this.setState({ selectedKey: `idle-${p.id}` })}
                                                                />
                                                            ) : null)}
                                                        </>
                                                    )}
                                            </GoogleMarkerClusterer>
                                            {selectedIdle ? (
                                                <InfoWindow
                                                    position={{ lat: selectedIdle.p.lat as number, lng: selectedIdle.p.lng as number }}
                                                    options={{ pixelOffset: new (window as any).google.maps.Size(0, -38) }}
                                                    onCloseClick={() => this.setState({ selectedKey: null })}
                                                >
                                                    <div style={{ minWidth: 160 }}>
                                                        <strong>{selectedIdle.p.name || 'Photographer'}</strong>
                                                        <div style={{ fontSize: 12, fontWeight: 700, color: selectedIdle.color }}>{selectedIdle.label}</div>
                                                        {selectedIdle.p.rating != null ? <div style={{ fontSize: 12, color: '#666' }}>★ {selectedIdle.p.rating}</div> : null}
                                                        {selectedIdle.p.contact ? <div style={{ fontSize: 12, color: '#666' }}>{selectedIdle.p.contact}</div> : null}
                                                        <div style={{ fontSize: 10.5, color: '#999', marginTop: 4 }}>Pin is at registered address</div>
                                                    </div>
                                                </InfoWindow>
                                            ) : null}
                                        </GoogleMap>
                                    </LoadScript>
                                    {visiblePinCount === 0 ? (
                                        <div style={{
                                            position: 'absolute', top: 14, left: '50%', transform: 'translateX(-50%)',
                                            background: 'rgba(20,20,28,0.92)', color: '#fff', fontSize: 12.5, fontWeight: 700,
                                            padding: '8px 16px', borderRadius: 10, boxShadow: '0 2px 10px rgba(0,0,0,0.35)', pointerEvents: 'none',
                                        }}>
                                            {EMPTY_VIEW_TEXT[mapFilter] || 'Nothing to show'}
                                        </div>
                                    ) : null}
                                    </div>
                                    <p style={{ fontSize: 10.5, color: '#777', fontStyle: 'italic', textAlign: 'center', paddingTop: 8 }}>
                                        Purple = shoot venue · colored pin = photographer on a shoot · line links each pair. Blue = available, grey = unavailable (shown at their registered address) · numbers = several pins close together, zoom in to split.
                                    </p>
                                    <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', justifyContent: 'center', fontSize: 10.5, color: '#999' }}>
                                        {[
                                            ...Object.entries(REACH_LABEL).map(([key, label]) => ({ key, label, color: REACH_COLOR[key] })),
                                            { key: 'idle-a', label: 'Available', color: IDLE_AVAILABLE_COLOR },
                                            { key: 'idle-u', label: 'Unavailable', color: IDLE_UNAVAILABLE_COLOR },
                                        ].map((item) => (
                                            <span key={item.key} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                                <span style={{ width: 8, height: 8, borderRadius: '50%', background: item.color }} />
                                                {item.label}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Roster panel */}
                            <div className="card">
                                <div className="card-body">
                                    <div style={pillBarStyle}>
                                        <span style={{ ...pillStyle(rosterTab === 'shoots'), flex: 1, textAlign: 'center' }} onClick={() => this.setFilter('shoots')}>
                                            Active <span style={countPillStyle}>{activeShoots.length}</span>
                                        </span>
                                        <span style={{ ...pillStyle(rosterTab === 'available'), flex: 1, textAlign: 'center' }} onClick={() => this.setFilter('available')}>
                                            Available <span style={countPillStyle}>{idleAvailable.length}</span>
                                        </span>
                                        <span style={{ ...pillStyle(rosterTab === 'unavailable'), flex: 1, textAlign: 'center' }} onClick={() => this.setFilter('unavailable')}>
                                            Unavailable <span style={countPillStyle}>{idleUnavailable.length}</span>
                                        </span>
                                    </div>

                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, maxHeight: 480, overflowY: 'auto' }}>
                                        {rosterTab === 'shoots' ? (
                                            visibleShoots.length ? visibleShoots.map((s) => (
                                                <RosterRow
                                                    key={s.receiptId}
                                                    selected={selectedKey === `photog-${s.receiptId}` || selectedKey === `venue-${s.receiptId}`}
                                                    onClick={() => s.photographer.lat != null && s.photographer.lng != null
                                                        ? this.focusPoint(s.photographer.lat, s.photographer.lng, 16, `photog-${s.receiptId}`)
                                                        : this.focusPoint(s.venue?.lat ?? null, s.venue?.lng ?? null, 16, `venue-${s.receiptId}`)}
                                                    name={s.photographer.name || 'Photographer'}
                                                    contact={s.photographer.contact}
                                                    meta={`#${s.receiptId} · ${s.customerName || ''}`}
                                                    badgeColor={REACH_COLOR[s.reach]}
                                                    badgeLabel={REACH_LABEL[s.reach] || s.reach}
                                                    sub={s.distanceMeters != null ? `${Math.round(s.distanceMeters)}m from venue` : undefined}
                                                />
                                            )) : <EmptyRoster text={mapFilter === 'off' ? 'No photographers with location off' : 'No active shoots right now'} />
                                        ) : rosterTab === 'available' ? (
                                            idleAvailable.length ? idleAvailable.map((p) => (
                                                <RosterRow key={p.id} selected={selectedKey === `idle-${p.id}`} onClick={() => this.focusPoint(p.lat, p.lng, 17, `idle-${p.id}`)} name={p.name || 'Photographer'} contact={p.contact} meta="Available now" sub={p.rating != null ? `★ ${p.rating}` : undefined} />
                                            )) : <EmptyRoster text="No available photographers" />
                                        ) : (
                                            idleUnavailable.length ? idleUnavailable.map((p) => (
                                                <RosterRow key={p.id} selected={selectedKey === `idle-${p.id}`} onClick={() => this.focusPoint(p.lat, p.lng, 17, `idle-${p.id}`)} name={p.name || 'Photographer'} contact={p.contact} meta="Unavailable today" sub={p.rating != null ? `★ ${p.rating}` : undefined} />
                                            )) : <EmptyRoster text="No unavailable photographers" />
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </AdminShell>
        );
    }
}

const RosterRow: React.FC<{ name: string; contact: string | null; meta: string; sub?: string; badgeColor?: string; badgeLabel?: string; onClick?: () => void; selected?: boolean }> = ({ name, contact, meta, sub, badgeColor, badgeLabel, onClick, selected }) => (
    <div
        onClick={onClick}
        title={onClick ? 'Show on map' : undefined}
        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 6px', borderBottom: '1px solid rgba(255,255,255,0.06)', cursor: onClick ? 'pointer' : 'default', borderRadius: 8, background: selected ? 'rgba(0,144,231,0.14)' : 'transparent' }}
    >
        <div style={{
            width: 34, height: 34, borderRadius: '50%', background: 'rgba(255,255,255,0.08)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800, flexShrink: 0,
        }}>
            {name.slice(0, 1).toUpperCase()}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12.5, fontWeight: 800 }}>{name}</div>
            <div style={{ fontSize: 10.5, opacity: 0.6, fontWeight: 600 }}>{meta}</div>
            {badgeLabel ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 9.5, fontWeight: 800, padding: '2px 7px', borderRadius: 6, marginTop: 3, background: `${badgeColor}22`, color: badgeColor }}>
                    <span style={{ width: 5, height: 5, borderRadius: '50%', background: badgeColor }} />
                    {badgeLabel}
                </span>
            ) : sub ? (
                <div style={{ fontSize: 10.5, opacity: 0.5, marginTop: 1 }}>{sub}</div>
            ) : null}
        </div>
        <CallButton contact={contact} />
    </div>
);

const EmptyRoster: React.FC<{ text: string }> = ({ text }) => (
    <div style={{ textAlign: 'center', color: '#777', fontSize: 12, fontWeight: 600, padding: '22px 0' }}>{text}</div>
);

const mapStateToProps = (state: any) => ({
    token: state.auth?.token,
});

export default connect(mapStateToProps)(LiveTrackingMap);
